import {
  DEFAULT_LIMIT_POLICY,
  limitPolicySchema,
  roomResponseSchema,
  type LimitPolicy,
  type RoomResponse,
  type RoomWorkstation,
} from '@growup/arena-shared';
import { Button, Card, Field, Input, Notice, StatusBadge, Table } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { PublicInfoCard } from './PublicInfoCard';

const POLICY_FIELDS: { key: keyof LimitPolicy; label: string }[] = [
  { key: 'maxContinuousMinutes', label: 'Máximo seguido (min)' },
  { key: 'breakMinutes', label: 'Pausa obrigatória (min)' },
  { key: 'maxDailyMinutes', label: 'Máximo por dia (min)' },
  { key: 'associateExtraMinutes', label: 'Extra para associados (min/dia)' },
  { key: 'meritExtraMinutes', label: 'Extra por bom aproveitamento (min/dia)' },
  { key: 'warnBeforeMinutes', label: 'Avisar antes do limite (min)' },
];

/** The selected arena: limits, PCs and what the site shows. Administrators only. */
export function ArenaConfigView({ arenaId }: { arenaId: string }) {
  return (
    <div className="flex flex-col gap-4">
      <ArenaSettings arenaId={arenaId} />
      <PublicInfoCard arenaId={arenaId} />
    </div>
  );
}

function ArenaSettings({ arenaId }: { arenaId: string }) {
  const [room, setRoom] = useState<RoomResponse | null>(null);
  const [policy, setPolicy] = useState<LimitPolicy>(DEFAULT_LIMIT_POLICY);
  const [pcName, setPcName] = useState('');
  const [token, setToken] = useState<{ name: string; token: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    getJson(`/api/arena/arenas/${arenaId}/room`, roomResponseSchema)
      .then((r) => {
        setRoom(r);
        setPolicy(r.policy);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [arenaId]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setSaved(false);
    try {
      await fn();
      load();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <>
      <Card headingLevel={2} title="Limites desta arena">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {POLICY_FIELDS.map((f) => (
            <Field key={f.key} label={f.label}>
              <Input
                type="number"
                min={0}
                value={policy[f.key]}
                onChange={(e) => setPolicy((p) => ({ ...p, [f.key]: Number(e.target.value) }))}
              />
            </Field>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button
            onClick={() =>
              void run(async () => {
                await send(
                  'PUT',
                  `/api/arena/arenas/${arenaId}/policy`,
                  limitPolicySchema.parse(policy),
                );
                setSaved(true);
              })
            }
          >
            Guardar limites
          </Button>
          {saved ? <StatusBadge tone="success">Guardado</StatusBadge> : null}
        </div>
      </Card>

      <Card headingLevel={2} title="Postos">
        {token ? (
          <Notice
            tone="warning"
            title={`Token do ${token.name}: copie-o agora para a configuração do agente.`}
          >
            <p>Não volta a ser mostrado. Se se perder, gere outro.</p>
            <code className="mt-2 block break-all bg-gu-sunken p-2 font-mono text-gu-ink">
              {token.token}
            </code>
            <Button variant="secondary" size="sm" className="mt-2" onClick={() => setToken(null)}>
              Já copiei
            </Button>
          </Notice>
        ) : null}
        <Table
          caption="Postos desta arena"
          rowKey={(w) => w.id}
          rows={room?.workstations ?? []}
          empty="Ainda não há postos."
          columns={[
            { key: 'name', header: 'Posto', cell: (w) => w.name },
            ...(['posX', 'posY'] as const).map((k) => ({
              key: k,
              header: k === 'posX' ? 'X %' : 'Y %',
              cell: (w: RoomWorkstation) => (
                <Input
                  type="number"
                  min={0}
                  max={100}
                  aria-label={`${k} ${w.name}`}
                  defaultValue={w[k]}
                  className="min-w-20 max-w-28"
                  onBlur={(e) =>
                    void run(() =>
                      send('PATCH', `/api/arena/workstations/${w.id}`, {
                        [k]: Number(e.target.value),
                      }),
                    )
                  }
                />
              ),
            })),
            {
              key: 'token',
              header: <span className="sr-only">Token</span>,
              align: 'right' as const,
              cell: (w: RoomWorkstation) => (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void run(async () => {
                      const r = (await send('POST', `/api/arena/workstations/${w.id}/token`)) as {
                        token: string;
                      };
                      setToken({ name: w.name, token: r.token });
                    })
                  }
                >
                  Novo token
                </Button>
              ),
            },
          ]}
        />
        <div className="mt-3 flex items-end gap-2">
          <Field label="Novo posto">
            <Input placeholder="PC13" value={pcName} onChange={(e) => setPcName(e.target.value)} />
          </Field>
          <Button
            disabled={pcName.trim() === ''}
            onClick={() =>
              void run(async () => {
                const r = (await send('POST', `/api/arena/arenas/${arenaId}/workstations`, {
                  name: pcName.trim(),
                })) as {
                  token: string;
                };
                setToken({ name: pcName.trim(), token: r.token });
                setPcName('');
              })
            }
          >
            Criar posto
          </Button>
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
      </Card>
    </>
  );
}
